/**
 * Whether the app may mark the menu when a newer version exists (D266 §3.5).
 *
 * **It is a mute, not an off — and the difference is the whole decision.** `mute` silences the
 * pulsing dot on the hamburger; **the Update row stays in the sidebar**, because that row is the
 * only route this product has to the store. A setting that removed it would not be *"stop telling
 * me"*, it would be *"take the update away"*, which is not a thing a resident asked for and not a
 * thing this app is entitled to do on their behalf.
 *
 * **The setting governs the report, never the check.** `storeUpdate.ts` is untouched by this
 * module: `check()` still runs on its six-hour floor, and it is `useAppUpdate` that stops raising
 * the flag. Three reasons, and none of them is tidiness.
 *
 *  - **A download in flight must still resolve.** Stop the check while `phase === 'downloading'`
 *    and the poll stops with it, so the row can never reach *Restart to finish* — a downloaded
 *    update stranded on the device with no way to install it.
 *  - **Turning it back on must be immediate.** If mute stopped the check, a resident who
 *    re-enabled alerts would wait up to six hours to learn what the app already knew a minute ago,
 *    and the setting would look broken rather than slow.
 *  - **It keeps one thing testable.** The rail's phase machine has one input (the plugin) and this
 *    preference has one consumer (the hook). Braiding them gives `storeUpdate.test.ts` a second
 *    axis for no behaviour.
 *
 * **Mute is absolute, including on `downloaded`.** The tempting exception is the phase where the
 * binary is already on the device and one tap from installed — surely *that* deserves a dot. It
 * does not: **if we will not force an update (D266 §2.1's refusal of `AppUpdateType.IMMEDIATE`), we
 * do not get to force a notification about one either.** A preference a feature overrules at its
 * own discretion is not a preference. 🔸 The stated cost is that a muted resident can leave a
 * downloaded update pending indefinitely.
 *
 * **It applies to BOTH rails, deliberately.** The preference sits in `useAppUpdate`, above the
 * fork, so it silences the PWA's waiting-shell dot as well as the container's. One setting, one
 * meaning — *tell me when there is an update* — because it is the same dot on the same app bar and
 * a resident neither knows nor cares which rail is behind it. Two settings differing only by
 * delivery mechanism would be the product leaking its own architecture.
 *
 * **Device-local, in the shape of `theme/colorScheme.ts` and `theme/openAnimation.ts` and on their
 * argument**: this is a property of *this handset*, there is no account field for it, and D255
 * makes minting one a scope decision rather than a screen's — which is why it is a clause of D266
 * rather than a thing the preferences screen grew.
 */
import { STAGE } from '../auth/config'

export type UpdateAlerts = 'popover' | 'menu' | 'mute'

/**
 * **D285 — one question with three answers; the launch prompt was the default until D345.**
 *
 * D266 shipped two values because there was one surface to govern: the hamburger dot. D285 adds a
 * second — a once-per-launch `ConfirmPopover` on Home — and rather than a second preference beside
 * this one, the pill widens: it is one question (*how do you want to be told?*) with three answers,
 * and two settings differing only by surface would be the product leaking its own architecture,
 * which is this module's own argument for putting both rails behind one value.
 *
 * `popover` — the prompt, plus the dot and the row behind it. `menu` — D266's behaviour: the dot
 * and the row, and (since D345) the prompt only when the operator's soft floor marks this build
 * urgent. `mute` — unchanged in every particular.
 *
 * **Nothing migrates, and that is the whole of the migration.** A device holding D266's `'show'`
 * fails `isAlerts` and reads as the default — never `mute`, which is the one answer that would
 * take something away. That is D198 (c)'s mechanism, and it is why a shortened or re-spelled union
 * needs no migration path of its own. (Under D285 the default was the louder answer, so those
 * residents were told louder; D345 below turns that direction.)
 *
 * **D345 — the default is `menu`, and `menu` now prompts for an URGENT update.**
 *
 * D285 made every new version interrupt the resident once per launch — including the PWA after
 * every web deploy — while only the operator can tell an urgent release from a routine one. So
 * urgency moved to the remote dial (`prompt_build_number`, `config/promptFloor.ts`) and the default
 * moved to the answer that consults it: a routine update raises the dot and the row, and one below
 * the operator's soft floor raises the prompt as well. `popover` is now the explicit opt-in to be
 * prompted for **every** update, and `mute` is unchanged in every particular.
 *
 * **The migration is still the absence of one, and D345 (c) turns its direction.** A device with
 * nothing stored reads the default and moves to `menu` — the population the complaint came from.
 * A stored `popover` is kept, since this preference is written only on a tap, so it is a choice.
 * D266's `'show'` still fails `isAlerts` and falls to the default, which is now `menu`: exactly
 * what `'show'` meant under D266, so the one group that had asked to be told loses nothing.
 */
export const DEFAULT_UPDATE_ALERTS: UpdateAlerts = 'menu'

/**
 * The order the preferences pill lays them out: loudest to quietest. D345 moved the default to the
 * middle answer and deliberately left the order alone — the pill marks the stored choice rather
 * than the default, and `menu` between the two is what makes it read as the quieter one.
 */
export const UPDATE_ALERTS_OPTIONS: readonly UpdateAlerts[] = ['popover', 'menu', 'mute']

const alertsKey = (stage: string): string => `selogate.${stage}.update-alerts`

function isAlerts(value: unknown): value is UpdateAlerts {
  return value === 'popover' || value === 'menu' || value === 'mute'
}

/**
 * The stored preference, or the default. **Never throws** — a private-mode browser throws on
 * `getItem` rather than returning `null` (`auth/credentials.ts`'s rule), and an unrecognised stored
 * value falls back to the default, which is D198 (c)'s mechanism and is also the whole of the
 * migration if this union ever changes.
 */
export function readUpdateAlerts(stage: string = STAGE): UpdateAlerts {
  try {
    const raw = globalThis.localStorage?.getItem(alertsKey(stage))
    return isAlerts(raw) ? raw : DEFAULT_UPDATE_ALERTS
  } catch {
    return DEFAULT_UPDATE_ALERTS
  }
}

let state: UpdateAlerts = readUpdateAlerts()
const listeners = new Set<() => void>()

/**
 * **Unlike its two siblings this module publishes, and the asymmetry is the thing to understand.**
 *
 * `colorScheme.ts` needs no listeners because it writes to the DOM — `data-scheme` on `<html>`
 * repaints without React. `openAnimation.ts` needs none because Home reads it at the moment an
 * overlay mounts. This preference's consumer is `AppBar`, a component that is **already mounted and
 * elsewhere on the screen**, so a bare `localStorage` write moves nothing: the dot would sit there
 * until the next unrelated re-render — green in every test that mounts fresh, wrong on the device.
 */
export function subscribeToUpdateAlerts(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** `useSyncExternalStore`'s snapshot. A string, so reference stability is free. */
export function getUpdateAlerts(): UpdateAlerts {
  return state
}

export function writeUpdateAlerts(next: UpdateAlerts, stage: string = STAGE): void {
  try {
    globalThis.localStorage?.setItem(alertsKey(stage), next)
  } catch {
    /* ignore — see readUpdateAlerts */
  }
  // **Published even when the write threw.** The resident chose this on a screen that is showing
  // them the choice, and a private-mode browser losing it at the next launch is a smaller failure
  // than a pill that appears to do nothing now.
  if (next === state) return
  state = next
  for (const listener of listeners) listener()
}

/** Test seam. Notifies rather than dropping listeners — `storeUpdate.ts` records why. */
export function __resetUpdateAlerts(): void {
  writeUpdateAlerts(DEFAULT_UPDATE_ALERTS)
}
